<template>
  <div ref="rootRef" class="network-detail" tabindex="0" data-testid="network-detail-panel" @keyup.esc="$emit('close')">
    <!-- Header bar: back button + method + status + URL -->
    <div class="network-detail__header">
      <button ref="backBtnRef" type="button" class="network-detail__back" data-testid="network-detail-back" @click="$emit('close')">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        <span class="leading-none">Back</span>
      </button>
      <span class="network-detail__method">{{ entry.method }}</span>
      <span class="network-detail__status" :class="statusClass">{{ entry.status }}</span>
      <span class="network-detail__url" :title="entry.url">{{ entry.url }}</span>
      <button type="button" class="network-detail__jump" data-testid="network-detail-jump" @click="$emit('jump', entry.timestamp)">
        Jump to {{ formatLogTimestamp(entry.timestamp) }}
      </button>
    </div>

    <!-- Internal sub-tabs -->
    <div class="network-detail__subtabs" role="tablist">
      <button
        v-for="tab in subTabs"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeSubTab === tab.id"
        :data-testid="`network-detail-subtab-${tab.id}`"
        class="network-detail__subtab"
        :class="{ 'network-detail__subtab--active': activeSubTab === tab.id }"
        @click="activeSubTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- Content -->
    <div class="network-detail__content">
      <!-- Headers -->
      <template v-if="activeSubTab === 'headers'">
        <section class="network-detail__section">
          <h3 class="network-detail__section-title">Request</h3>
          <div v-if="hasRequestHeaders" class="network-detail__headers">
            <div v-for="(value, name) in detail.request.headers" :key="`req-${name}`" class="network-detail__header-row">
              <span class="network-detail__header-name">{{ name }}</span>
              <span class="network-detail__header-value">{{ value }}</span>
            </div>
          </div>
          <p v-else class="network-detail__empty">(no request headers captured)</p>
        </section>

        <section class="network-detail__section">
          <h3 class="network-detail__section-title">Response</h3>
          <div v-if="hasResponseHeaders" class="network-detail__headers">
            <div v-for="(value, name) in detail.response.headers" :key="`res-${name}`" class="network-detail__header-row">
              <span class="network-detail__header-name">{{ name }}</span>
              <span class="network-detail__header-value">{{ value }}</span>
            </div>
          </div>
          <p v-else class="network-detail__empty">(no response headers captured)</p>
        </section>
      </template>

      <!-- Response body -->
      <template v-else-if="activeSubTab === 'response'">
        <p v-if="!detail.response.body" class="network-detail__empty">(no response body captured)</p>
        <pre v-else class="network-detail__body">{{ detail.response.body }}</pre>
      </template>

      <!-- Timing -->
      <template v-else-if="activeSubTab === 'timing'">
        <div class="network-detail__timing">
          <div class="network-detail__timing-row">
            <span class="network-detail__timing-label">Total</span>
            <span class="network-detail__timing-value">{{ detail.timings.total }} ms</span>
          </div>
          <p class="network-detail__timing-note">Detailed breakdown (DNS, connect, etc.) not captured by the SDK.</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick } from 'vue';
import type { NetworkEntry } from '@/modules/viewer/types/logs';
import { formatLogTimestamp } from '@/modules/viewer/composables/useLogs';

const props = defineProps<{
  entry: NetworkEntry;
}>();

defineEmits<{
  close: [];
  jump: [timestamp: number];
}>();

type SubTab = 'headers' | 'response' | 'timing';

const subTabs: Array<{ id: SubTab; label: string }> = [
  { id: 'headers', label: 'Headers' },
  { id: 'response', label: 'Response' },
  { id: 'timing', label: 'Timing' },
];

const activeSubTab = ref<SubTab>('headers');
const rootRef = ref<HTMLElement | null>(null);
const backBtnRef = ref<HTMLElement | null>(null);

// Pre-computed at transform time, so this is a cheap read.
const detail = computed(() => {
  // The detail field is optional in the type for back-compat with mocks,
  // but in practice every real entry has it. Provide a defensive fallback.
  return (
    props.entry.detail ?? {
      request: { method: props.entry.method, url: props.entry.url, headers: {} },
      response: { status: props.entry.status, statusText: '', headers: {}, body: undefined, contentType: undefined },
      timings: { total: props.entry.duration },
    }
  );
});

const hasRequestHeaders = computed(() => Object.keys(detail.value.request.headers).length > 0);
const hasResponseHeaders = computed(() => Object.keys(detail.value.response.headers).length > 0);

const statusClass = computed(() => {
  const s = props.entry.status;
  if (s >= 500) return 'network-detail__status--error';
  if (s >= 400) return 'network-detail__status--warn';
  if (s >= 300) return 'network-detail__status--redirect';
  return 'network-detail__status--success';
});

onMounted(async () => {
  // Focus the back button so Esc + tab navigation work without a manual click.
  await nextTick();
  backBtnRef.value?.focus();
});
</script>

<style scoped>
.network-detail {
  @apply flex flex-col h-full focus:outline-none;
}

.network-detail__header {
  @apply flex items-center gap-3 px-4 py-2 border-b border-zinc-200 dark:border-zinc-700;
}

.network-detail__back {
  @apply flex items-center gap-1 px-2 py-1 rounded-md text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 cursor-pointer;
}

.network-detail__method {
  @apply text-xs font-mono font-semibold text-zinc-600 dark:text-zinc-400 flex-shrink-0;
}

.network-detail__status {
  @apply inline-flex items-center justify-center min-w-[40px] px-1.5 py-0.5 text-xs font-semibold rounded flex-shrink-0;
}

.network-detail__status--success {
  @apply bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400;
}
.network-detail__status--redirect {
  @apply bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400;
}
.network-detail__status--warn {
  @apply bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400;
}
.network-detail__status--error {
  @apply bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400;
}

.network-detail__url {
  @apply text-sm text-zinc-800 dark:text-zinc-200 truncate min-w-0 flex-1;
}

.network-detail__jump {
  @apply text-xs font-medium px-2 py-1 rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer flex-shrink-0;
}

.network-detail__subtabs {
  @apply flex items-center gap-1 px-4 border-b border-zinc-200 dark:border-zinc-700;
}

.network-detail__subtab {
  @apply flex items-center justify-center px-3 py-2 text-sm font-medium cursor-pointer transition-colors relative;
  @apply text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200;
}

.network-detail__subtab--active {
  @apply text-blue-600 dark:text-blue-400;
}

.network-detail__subtab--active::after {
  content: '';
  @apply absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400;
}

.network-detail__content {
  @apply flex-grow min-h-0 overflow-y-auto px-4 py-3;
}

.network-detail__section {
  @apply mb-4;
}

.network-detail__section-title {
  @apply text-xs font-semibold uppercase text-zinc-500 dark:text-zinc-400 mb-2;
}

.network-detail__headers {
  @apply flex flex-col gap-1;
}

.network-detail__header-row {
  @apply flex items-start gap-3 text-xs;
}

.network-detail__header-name {
  @apply font-mono font-semibold text-zinc-700 dark:text-zinc-300 flex-shrink-0;
  min-width: 120px;
}

.network-detail__header-value {
  @apply font-mono text-zinc-600 dark:text-zinc-400 break-all min-w-0;
}

.network-detail__empty {
  @apply text-sm text-zinc-500 dark:text-zinc-400 italic;
}

.network-detail__body {
  @apply text-xs font-mono text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap break-words;
}

.network-detail__timing {
  @apply flex flex-col gap-2;
}

.network-detail__timing-row {
  @apply flex items-center justify-between text-sm;
}

.network-detail__timing-label {
  @apply font-semibold text-zinc-700 dark:text-zinc-300;
}

.network-detail__timing-value {
  @apply font-mono text-zinc-800 dark:text-zinc-200;
}

.network-detail__timing-note {
  @apply text-xs text-zinc-500 dark:text-zinc-400 mt-2 italic;
}
</style>
